import React, { useState, useRef, useEffect } from "react";
import { cn } from "../utils/cn";
import { motion, AnimatePresence } from "framer-motion";
import HeroOverlay from "./HeroOverlay";

const SplitViewport = ({
  leftImage,
  rightImage,
  leftAlt = "Left image",
  rightAlt = "Right image",
  className,
  onProfessionalClick,
  onHouseholdClick,
}) => {
  const [hoveredSide, setHoveredSide] = useState("none");
  const [isDividerHovered, setIsDividerHovered] = useState(false);
  const [activeView, setActiveView] = useState("none");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileActiveSide, setMobileActiveSide] = useState('right');
  const leftForwardVideoRef = useRef(null);
  const leftReverseVideoRef = useRef(null);
  const rightForwardVideoRef = useRef(null);
  const rightReverseVideoRef = useRef(null);
  const [showLeftForward, setShowLeftForward] = useState(true);
  const [showRightForward, setShowRightForward] = useState(true);
  const [leftVideoLoaded, setLeftVideoLoaded] = useState(false);
  const [rightVideoLoaded, setRightVideoLoaded] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle mobile auto-switch
  useEffect(() => {
    if (!isMobile) return;

    const interval = setInterval(() => {
      setMobileActiveSide(prev => prev === 'left' ? 'right' : 'left');
    }, 5000);

    return () => clearInterval(interval);
  }, [isMobile]);

  // Handle video playback based on mobile active side
  useEffect(() => {
    if (!isMobile) return;

    const leftForwardVideo = leftForwardVideoRef.current;
    const leftReverseVideo = leftReverseVideoRef.current;
    const rightForwardVideo = rightForwardVideoRef.current;
    const rightReverseVideo = rightReverseVideoRef.current;

    if (mobileActiveSide === 'left') {
      // Pause right videos
      rightForwardVideo?.pause();
      rightReverseVideo?.pause();
      // Play left videos if they were playing
      if (showLeftForward) {
        leftForwardVideo?.play().catch(() => {});
      } else {
        leftReverseVideo?.play().catch(() => {});
      }
    } else {
      // Pause left videos
      leftForwardVideo?.pause();
      leftReverseVideo?.pause();
      // Play right videos if they were playing
      if (showRightForward) {
        rightForwardVideo?.play().catch(() => {});
      } else {
        rightReverseVideo?.play().catch(() => {});
      }
    }
  }, [isMobile, mobileActiveSide, showLeftForward, showRightForward]);

  const isVideo = (src) => {
    return src.endsWith('.mp4') || src.endsWith('.webm');
  };

  // Function to optimize Contentful video URLs
  const optimizeVideoUrl = (url) => {
    if (!url.includes('ctfassets.net')) return url;
    
    // Add query parameters for optimization
    const optimizedUrl = new URL(url);
    optimizedUrl.searchParams.set('q', isMobile ? '60' : '70'); // Slightly higher quality for mobile
    optimizedUrl.searchParams.set('fm', 'mp4'); // Ensure MP4 format
    optimizedUrl.searchParams.set('w', isMobile ? '640' : '1280'); // Higher resolution for mobile
    return optimizedUrl.toString();
  };

  useEffect(() => {
    if (!isVideo(leftImage)) return;

    const forwardVideo = leftForwardVideoRef.current;
    const reverseVideo = leftReverseVideoRef.current;
    if (!forwardVideo || !reverseVideo) return;

    const handleForwardEnd = () => {
      if (!reverseVideo.duration || !isFinite(reverseVideo.duration)) return;
      setShowLeftForward(false);
      reverseVideo.currentTime = reverseVideo.duration;
      reverseVideo.play().catch(() => {
        // If play fails, try to resume after a short delay
        setTimeout(() => reverseVideo.play(), 100);
      });
    };

    const handleReverseEnd = () => {
      setShowLeftForward(true);
      forwardVideo.currentTime = 0;
      forwardVideo.play().catch(() => {
        // If play fails, try to resume after a short delay
        setTimeout(() => forwardVideo.play(), 100);
      });
    };

    const handleVideoError = (error) => {
      console.warn('Video playback error:', error);
      // Try to recover by resetting the video
      if (error.target === forwardVideo) {
        forwardVideo.load();
        forwardVideo.play().catch(() => {});
      } else if (error.target === reverseVideo) {
        reverseVideo.load();
        reverseVideo.play().catch(() => {});
      }
    };

    forwardVideo.addEventListener('ended', handleForwardEnd);
    reverseVideo.addEventListener('ended', handleReverseEnd);
    forwardVideo.addEventListener('error', handleVideoError);
    reverseVideo.addEventListener('error', handleVideoError);

    // Set video properties
    forwardVideo.muted = true;
    reverseVideo.muted = true;
    forwardVideo.preload = "metadata";
    reverseVideo.preload = "metadata";

    // Start playing the forward video
    forwardVideo.play().catch(() => {
      // If initial play fails, try again after a short delay
      setTimeout(() => forwardVideo.play(), 100);
    });

    return () => {
      forwardVideo.removeEventListener('ended', handleForwardEnd);
      reverseVideo.removeEventListener('ended', handleReverseEnd);
      forwardVideo.removeEventListener('error', handleVideoError);
      reverseVideo.removeEventListener('error', handleVideoError);
    };
  }, [leftImage]);

  useEffect(() => {
    if (!isVideo(rightImage)) return;

    const forwardVideo = rightForwardVideoRef.current;
    const reverseVideo = rightReverseVideoRef.current;
    if (!forwardVideo || !reverseVideo) return;

    const handleForwardEnd = () => {
      if (!reverseVideo.duration || !isFinite(reverseVideo.duration)) return;
      setShowRightForward(false);
      reverseVideo.currentTime = reverseVideo.duration;
      reverseVideo.play().catch(() => {
        // If play fails, try to resume after a short delay
        setTimeout(() => reverseVideo.play(), 100);
      });
    };

    const handleReverseEnd = () => {
      setShowRightForward(true);
      forwardVideo.currentTime = 0;
      forwardVideo.play().catch(() => {
        // If play fails, try to resume after a short delay
        setTimeout(() => forwardVideo.play(), 100);
      });
    };

    const handleVideoError = (error) => {
      console.warn('Video playback error:', error);
      // Try to recover by resetting the video
      if (error.target === forwardVideo) {
        forwardVideo.load();
        forwardVideo.play().catch(() => {});
      } else if (error.target === reverseVideo) {
        reverseVideo.load();
        reverseVideo.play().catch(() => {});
      }
    };

    forwardVideo.addEventListener('ended', handleForwardEnd);
    reverseVideo.addEventListener('ended', handleReverseEnd);
    forwardVideo.addEventListener('error', handleVideoError);
    reverseVideo.addEventListener('error', handleVideoError);

    // Set video properties
    forwardVideo.muted = true;
    reverseVideo.muted = true;
    forwardVideo.preload = "metadata";
    reverseVideo.preload = "metadata";

    // Start playing the forward video
    forwardVideo.play().catch(() => {
      // If initial play fails, try again after a short delay
      setTimeout(() => forwardVideo.play(), 100);
    });

    return () => {
      forwardVideo.removeEventListener('ended', handleForwardEnd);
      reverseVideo.removeEventListener('ended', handleReverseEnd);
      forwardVideo.removeEventListener('error', handleVideoError);
      reverseVideo.removeEventListener('error', handleVideoError);
    };
  }, [rightImage]);

  const handleProfessionalClick = () => {
    setActiveView("professional");
    setHoveredSide("left");
    onProfessionalClick?.();
  };

  const handleHouseholdClick = () => {
    setActiveView("household");
    setHoveredSide("right");
    onHouseholdClick?.();
  };

  const handleProfessionalHover = () => {
    setHoveredSide("left");
  };

  const handleHouseholdHover = () => {
    setHoveredSide("right");
  };

  const handleButtonLeave = () => {
    setHoveredSide("none");
  };

  const getLeftWidth = () => {
    if (isMobile) {
      if (mobileActiveSide === 'left') return "w-full";
      if (mobileActiveSide === 'right') return "w-0";
      if (activeView === "professional") return "w-full";
      if (activeView === "household") return "w-0";
      if (hoveredSide === "left") return "w-full";
      if (hoveredSide === "right") return "w-0";
      return "w-1/2";
    }
    
    if (activeView === "professional") return "w-[90%]";
    if (activeView === "household") return "w-[10%]";
    if (hoveredSide === "left") return "w-[90%]";
    if (hoveredSide === "right") return "w-[10%]";
    return "w-1/2";
  };

  const getRightWidth = () => {
    if (isMobile) {
      if (mobileActiveSide === 'left') return "w-0";
      if (mobileActiveSide === 'right') return "w-full";
      if (activeView === "professional") return "w-0";
      if (activeView === "household") return "w-full";
      if (hoveredSide === "left") return "w-0";
      if (hoveredSide === "right") return "w-full";
      return "w-1/2";
    }
    
    if (activeView === "professional") return "w-[10%]";
    if (activeView === "household") return "w-[90%]";
    if (hoveredSide === "left") return "w-[10%]";
    if (hoveredSide === "right") return "w-[90%]";
    return "w-1/2";
  };

  return (
    <div className={cn("h-screen w-screen flex overflow-hidden relative", className)}>
      {/* Left side */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        className={cn(
          "relative h-full overflow-hidden transition-all duration-500 ease-in-out",
          getLeftWidth()
        )}
      >
        <div className={cn(
          "absolute inset-0 transition-transform duration-500 ease-out",
          !isMobile && hoveredSide === "right" ? "scale-105" : "scale-100"
        )}>
          {isVideo(leftImage) ? (
            <>
              <video
                ref={leftForwardVideoRef}
                src={optimizeVideoUrl(leftImage)}
                className={cn(
                  "w-full h-full object-cover absolute inset-0",
                  showLeftForward ? "opacity-100" : "opacity-0",
                  "transform scale-x-[-1]"
                )}
                loop={false}
                muted
                playsInline
                preload="metadata"
                poster={isMobile ? optimizeVideoUrl(leftImage) + "&w=640" : undefined}
              />
              <video
                ref={leftReverseVideoRef}
                src={optimizeVideoUrl(leftImage)}
                className={cn(
                  "w-full h-full object-cover absolute inset-0",
                  !showLeftForward ? "opacity-100" : "opacity-0",
                  "transform scale-x-[-1]"
                )}
                loop={false}
                muted
                playsInline
                preload="metadata"
                poster={isMobile ? optimizeVideoUrl(leftImage) + "&w=640" : undefined}
              />
            </>
          ) : (
            <img
              src={leftImage}
              alt={leftAlt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )}
        </div>
      </motion.div>

      {/* SVG Mask Divider - Hide on mobile */}
      {!isMobile && (
        <motion.div 
          className={cn(
            "absolute top-0 left-1/2 h-full z-10 pointer-events-none",
            "flex items-center justify-center"
          )}
          style={{
            left: activeView === "professional" ? "calc(90% - 2px)" : 
                  activeView === "household" ? "calc(10% - 2px)" :
                  hoveredSide === "left" ? "calc(90% - 2px)" : 
                  hoveredSide === "right" ? "calc(10% - 2px)" : "50%"
          }}
          onMouseEnter={() => setIsDividerHovered(true)}
          onMouseLeave={() => setIsDividerHovered(false)}
        >
          <svg width="100%" height="100%" className="absolute inset-0">
            <defs>
              <linearGradient id="dividerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="transparent" />
                <stop offset="50%" stopColor="white" stopOpacity="0.08" />
                <stop offset="100%" stopColor="transparent" />
              </linearGradient>
              <filter id="dividerBlur">
                <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
              </filter>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="url(#dividerGradient)"
              filter="url(#dividerBlur)"
            />
          </svg>

          {/* Glass divider */}
          <motion.div
            className="h-full w-[4px] rounded-full bg-white/8 backdrop-blur-[8px]"
            initial={{ width: 4, opacity: 0.5 }}
            animate={{ 
              width: isDividerHovered ? 8 : 4,
              opacity: isDividerHovered ? 0.9 : 0.5,
              boxShadow: isDividerHovered 
                ? "0 0 20px rgba(255,255,255,0.3)" 
                : "0 0 15px rgba(255,255,255,0.15)"
            }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
      )}

      {/* Right side */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        className={cn(
          "relative h-full overflow-hidden transition-all duration-500 ease-in-out",
          getRightWidth()
        )}
      >
        <div className={cn(
          "absolute inset-0 transition-transform duration-500 ease-out",
          !isMobile && hoveredSide === "left" ? "scale-105" : "scale-100"
        )}>
          {isVideo(rightImage) ? (
            <>
              <video
                ref={rightForwardVideoRef}
                src={optimizeVideoUrl(rightImage)}
                className={cn(
                  "w-full h-full object-cover absolute inset-0",
                  showRightForward ? "opacity-100" : "opacity-0"
                )}
                loop={false}
                muted
                playsInline
                preload="metadata"
                poster={isMobile ? optimizeVideoUrl(rightImage) + "&w=640" : undefined}
              />
              <video
                ref={rightReverseVideoRef}
                src={optimizeVideoUrl(rightImage)}
                className={cn(
                  "w-full h-full object-cover absolute inset-0",
                  !showRightForward ? "opacity-100" : "opacity-0"
                )}
                loop={false}
                muted
                playsInline
                preload="metadata"
                poster={isMobile ? optimizeVideoUrl(rightImage) + "&w=640" : undefined}
                playbackRate={-1}
              />
            </>
          ) : (
            <img
              src={rightImage}
              alt={rightAlt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )}
        </div>
      </motion.div>

      {/* Hero Overlay */}
      <HeroOverlay 
        onProfessionalClick={handleProfessionalClick}
        onHouseholdClick={handleHouseholdClick}
        onProfessionalHover={handleProfessionalHover}
        onHouseholdHover={handleHouseholdHover}
        onButtonLeave={handleButtonLeave}
        isMobile={isMobile}
      />
    </div>
  );
};

export default SplitViewport; 